import type { Recipe } from '../types';

// Liam's grandpa's chili, kept intact and refined only where the technique was
// leaving flavor on the table. The Chili-O packets and the ground beef are not
// negotiable — they are the whole point. What changed is the order of
// operations and one teaspoon of vinegar at the end.

export const grandpasChili: Recipe = {
  slug: 'grandpas-chili',
  title: "Grandpa's Chili",
  meal: 'dinner',
  category: 'meal',
  types: ['stew'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'one-pot', 'family-recipe'],
  yield: '12 servings',
  activeTime: '25 min',
  totalTime: '1 hr 25 min',
  freezerLife: '4–6 months',
  difficulty: 1,

  summary:
    'The chili we all grew up on, with the order of operations fixed. Same packets, same Brooks beans, same everything on the shopping list — it just extracts about twice as much out of the spices as it used to.',

  flavorNotes:
    'This is Midwest chili and it is unapologetically bean-forward — anybody expecting Texas chili will be confused, and they are wrong. Expect it thick, mildly sweet from the bean sauce, warm rather than hot, with cumin sitting right out front where Grandpa put it. The heat comes entirely from the one hot can of Brooks and lands somewhere around a medium. If it tastes flat when it is done, it does not need more chili powder; it needs the vinegar and then salt, in that order.',

  ingredients: [
    {
      id: 'beef',
      name: 'Ground beef, 80/20 or 85/15',
      total: '3 lb',
      prep: 'nothing — goes in as-is',
      fromFreezer: true,
      uses: [{ step: 1, amount: '3 lb' }],
    },
    {
      id: 'onion',
      name: 'Yellow or white onion',
      total: '1 cup',
      prep: 'chopped',
      uses: [{ step: 2, amount: '1 cup' }],
    },
    {
      id: 'garlic',
      name: 'Minced garlic',
      total: '1½ tbsp',
      prep: 'jarred is fine',
      uses: [{ step: 2, amount: '1½ tbsp', note: 'last 30 seconds only' }],
    },
    {
      id: 'chili-o',
      name: "French's Original Chili-O seasoning",
      total: '3 packets',
      uses: [{ step: 4, amount: '3 packets' }],
    },
    {
      id: 'cumin',
      name: 'Ground cumin',
      total: '1½ tbsp, heaping',
      prep: "Grandpa's addition — do not reduce it",
      uses: [{ step: 4, amount: '1½ tbsp' }],
    },
    {
      id: 'broth',
      name: 'Beef broth',
      total: '1¼ cups',
      prep: 'Better Than Bouillon beef base, 1 tsp per cup of hot water',
      fromFreezer: true,
      uses: [{ step: 5, amount: '1¼ cups' }],
    },
    {
      id: 'worcestershire',
      name: 'Worcestershire sauce',
      total: '1½ tbsp',
      uses: [{ step: 5, amount: '1½ tbsp' }],
    },
    {
      id: 'beans',
      name: 'Brooks chili beans, 40 oz cans',
      total: '2 cans',
      prep: 'one MILD and one HOT — this is the whole heat control',
      uses: [{ step: 6, amount: '2 cans', note: 'sauce and all' }],
    },
    {
      id: 'tomatoes',
      name: 'Petite diced tomatoes, 28 oz',
      total: '1 can',
      prep: 'Dei Fratelli or Red Gold',
      uses: [{ step: 6, amount: '1 can', note: 'juice included' }],
    },
    {
      id: 'vinegar',
      name: 'Apple cider vinegar',
      total: '2 tsp',
      uses: [{ step: 8, amount: '2 tsp', note: 'off the heat, at the very end' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: 'to taste',
      uses: [{ step: 8, amount: 'to taste', note: 'after the beans, never before' }],
    },
    {
      id: 'bacon',
      name: 'Bacon',
      total: '4 slices',
      prep: 'chopped',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'Rendered first so its fat browns the beef. Showed up in more cook-off winners than any other single addition.',
      uses: [{ step: 1, amount: '4 slices', note: 'rendered first, fat used to brown the beef' }],
    },
    {
      id: 'cocoa',
      name: 'Unsweetened cocoa powder',
      total: '1 tbsp',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'Adds depth and roundness — it does not taste like chocolate. The most-cited cook-off secret there is.',
      uses: [{ step: 4, amount: '1 tbsp', note: 'depth, not chocolate flavor' }],
    },
    {
      id: 'smoked-paprika',
      name: 'Smoked paprika',
      total: '1 tsp',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'A fireside, over-a-fire note that the Chili-O packet has no way to deliver on its own.',
      uses: [{ step: 4, amount: '1 tsp' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Brown the beef by itself',
      time: '8 min',
      critical: true,
      ingredientIds: ['beef', 'bacon'],
      body:
        'Dutch oven over medium-high, no oil needed. Add the {3 lb|beef} of ground beef, break it into big pieces, then leave it alone for 4 to 5 minutes so one side actually browns before you stir it. Work in two batches if the pot looks crowded. If you are using the bacon, render it first, lift the bits out with a slotted spoon, and brown the beef in that fat.',
      tip:
        'The onions used to go in here alongside the raw beef. They release water on contact, the pan temperature drops, and the beef steams grey instead of browning. That browning is flavor you cannot add back in later.',
    },
    {
      n: 2,
      title: 'Now the onions and garlic',
      time: '5 min',
      ingredientIds: ['onion', 'garlic'],
      body:
        'Add the {1 cup|onion} of chopped onion to the browned beef and cook 4 to 5 minutes until it turns soft and translucent. Stir in the {1½ tablespoons|garlic} of minced garlic and give it 30 seconds — no longer, or it turns bitter.',
    },
    {
      n: 3,
      title: 'Drain — but keep two tablespoons',
      time: '2 min',
      critical: true,
      ingredientIds: [],
      body:
        'Drain the grease off exactly the way Grandpa did, then pour about {2 tablespoons} of it back into the pot. That is the entire change. Do not skip it — the next step does not work without fat in the pan.',
      tip:
        'He was right to drain it; nobody wants a greasy chili. Two tablespoons is the difference between spices that bloom and spices that just get wet.',
    },
    {
      n: 4,
      title: 'Bloom the spices in that fat',
      time: '2 min',
      critical: true,
      ingredientIds: ['chili-o', 'cumin', 'cocoa', 'smoked-paprika'],
      body:
        'Add the {3 packets|chili-o} of Chili-O and the {1½ tablespoons|cumin} of cumin straight onto the meat and the reserved fat, with no liquid in the pot yet — this is the bloom. If you are using them, the cocoa and smoked paprika go in here too. Stir constantly for 60 to 90 seconds, until the smell turns deep and toasty instead of dusty.',
      tip:
        'This is the single biggest upgrade in the whole recipe and it costs ninety seconds. Chili and cumin carry their flavor in fat-soluble compounds — blooming them in water, which is the original order, pulls out a fraction of what is actually in the packet.',
    },
    {
      n: 5,
      title: 'Broth in, reduce it down',
      time: '5 min',
      ingredientIds: ['broth', 'worcestershire'],
      body:
        'Pour in the {1¼ cups|broth} of beef broth and the {1½ tablespoons|worcestershire} of Worcestershire, and scrape the fond up off the bottom of the pot. Simmer about 5 minutes until it has nearly reduced away. This is Grandpa’s original step, untouched — it just carries flavor now instead of plain water.',
    },
    {
      n: 6,
      title: 'Beans and tomatoes',
      time: '2 min',
      ingredientIds: ['beans', 'tomatoes'],
      body:
        'Both cans of Brooks — the mild and the hot — with all of their sauce, plus the can of petite diced tomatoes and its juice. Stir it together and bring it up to a bare simmer.',
      tip:
        'The Brooks sauce is doing real work here. It is the reason this recipe has never needed a separate can of tomato sauce or paste.',
    },
    {
      n: 7,
      title: 'Simmer',
      time: '45–60 min',
      ingredientIds: [],
      body:
        'Uncovered, low, stirring every so often. You want a lazy blurp, not a boil. Longer than an hour and the Brooks beans start breaking down into mush, which is why this stops well short of the two hours a from-scratch chili wants.',
    },
    {
      n: 8,
      title: 'Vinegar, then salt — in that order',
      time: '2 min',
      critical: true,
      ingredientIds: ['vinegar', 'salt'],
      body:
        'Off the heat, stir in the {2 tsp|vinegar} of cider vinegar. Now taste it. Then decide about salt — between three packets of Chili-O and two cans of Brooks there is a great deal already in the pot, and most nights it needs none at all.',
      tip:
        'You will not taste the vinegar. Everything else just gets louder. It is the fix for chili that tastes flat in a way you cannot name.',
    },
    {
      n: 9,
      title: 'Eat now, or cool and pack',
      time: '30 min, mostly waiting',
      ingredientIds: [],
      body:
        'It is good tonight and better tomorrow — the spices keep moving after the heat goes off. For the freezer, let it cool completely before it goes in a bag, about 30 minutes. Packing it warm makes ice crystals.',
    },
  ],

  freezing:
    'Quart bags, pressed flat, air squeezed out, up to 4–6 months. Flat bags thaw in a sink of warm water in about 20 minutes, which is the whole reason to bother pressing them. Chili is one of the few things that comes out of the freezer genuinely indistinguishable from fresh.',

  reheat: [
    {
      method: 'Stovetop',
      best: true,
      detail:
        'Thaw overnight in the fridge, then into a saucepan over medium-low with a splash of water, stirring occasionally, about 8 minutes. The beans stay whole and the texture is identical to day one.',
    },
    {
      method: 'Microwave',
      detail:
        'Thaw overnight, then covered at 70% power in 90-second rounds, stirring between each. Full power boils the edges into mush before the middle is warm.',
    },
    {
      method: 'From frozen',
      detail:
        'Run the sealed bag under warm water for a minute to release the block, drop it into a covered saucepan on low with 2 tablespoons of water, and give it about 15 minutes, breaking it apart as it loosens.',
    },
  ],

  glossary: [
    {
      term: 'bloom',
      plain:
        'Toasting ground spices in hot fat for a minute before any liquid goes in. Most of what makes chili powder and cumin taste like anything is fat-soluble, so fat pulls those compounds out and then carries them through the whole pot. Water barely touches them. This is why the same packet can taste completely different depending on when you add it.',
    },
    {
      term: 'fond',
      plain:
        'The browned crust stuck to the bottom of the pot after searing meat. It looks like something you burned. It is not — it is concentrated flavor, and adding liquid dissolves it back into the food.',
    },
    {
      term: 'reduced',
      plain:
        'Simmered until much of the liquid has evaporated. What is left behind is more concentrated, so a reduced sauce tastes stronger than the same sauce did before it cooked down.',
    },
  ],

  inspiration: [
    { label: "Grandpa's original, as written", url: '' },
    { label: 'The Spice House — 12 secrets to perfect chili', url: 'https://www.thespicehouse.com/blogs/news/secret-to-perfect-chili' },
    { label: 'NU Sci — the science of blooming spices', url: 'https://www.nuscimagazine.com/when-flavors-bloom-what-happens-when-we-temper-spices' },
    { label: 'Panning the Globe — Eddie’s award-winning chili', url: 'https://www.panningtheglobe.com/eddies-award-winning-chili/' },
  ],

  whereWeVeered:
    'Almost nothing about the shopping list changed. The beef browns by itself before the onions go in; two tablespoons of the drained fat go back in the pot so the spices can bloom in fat instead of water; the cup of water became beef broth; the simmer went from 30 minutes to 45–60; and two teaspoons of cider vinegar go in off the heat at the end. We also moved the beef from 2 lb to 3 lb and the packets from 2 to 3 — at 2 lb the beef is about 18% of the pot by weight and gets a little lost behind five pounds of beans. Everything else, including both cans of Brooks and the heaping cumin, is exactly as he wrote it.',
};
